"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAuthRouteHandlersFactory = void 0;
const internals_1 = require("aws-amplify/adapter-core/internals");
const utils_1 = require("./utils");
const handleAuthApiRouteRequestForAppRouter_1 = require("./handleAuthApiRouteRequestForAppRouter");
const handleAuthApiRouteRequestForPagesRouter_1 = require("./handleAuthApiRouteRequestForPagesRouter");
const createAuthRouteHandlersFactory = ({ config: resourcesConfig, amplifyAppOrigin, runWithAmplifyServerContext, globalSettings, }) => {
    const handleRequest = async ({ request, contextOrResponse, handlerInput, userPoolClientId, oAuthConfig, setCookieOptions, origin, }) => {
        if ((0, utils_1.isNextApiRequest)(request) && (0, utils_1.isNextApiResponse)(contextOrResponse)) {
            // In pages router the response is sent via calling `response.end()` or
            // `response.send()`. The response is not returned from the handler.
            // To ensure these two methods are called before the handler returns,
            // we use `await` here.
            await (0, handleAuthApiRouteRequestForPagesRouter_1.handleAuthApiRouteRequestForPagesRouter)({
                request,
                response: contextOrResponse,
                handlerInput,
                userPoolClientId,
                oAuthConfig,
                setCookieOptions,
                origin,
                runWithAmplifyServerContext,
            });
            // In the Pages Router, the final response is handled by contextOrResponse
            return;
        }
        if ((0, utils_1.isNextRequest)(request) &&
            (0, utils_1.isAuthRoutesHandlersContext)(contextOrResponse)) {
            // In the App Router, the final response is constructed and returned
            return (0, handleAuthApiRouteRequestForAppRouter_1.handleAuthApiRouteRequestForAppRouter)({
                request,
                handlerContext: contextOrResponse,
                handlerInput,
                userPoolClientId,
                oAuthConfig,
                setCookieOptions,
                origin,
                runWithAmplifyServerContext,
            });
        }
        // this should not be happening
        throw new Error('Invalid request and context/response combination. The request cannot be handled.');
    };
    return (createAuthRoutesHandlersInput = {}) => {
        // origin validation should happen when createAuthRouteHandlers is being called to create
        // Auth API routes.
        if (!amplifyAppOrigin) {
            throw new internals_1.AmplifyServerContextError({
                message: 'Could not find the AMPLIFY_APP_ORIGIN environment variable.',
                recoverySuggestion: 'Add the AMPLIFY_APP_ORIGIN environment variable to the `.env` file of your Next.js project.',
            });
        }
        if (!(0, utils_1.isValidOrigin)(amplifyAppOrigin)) {
            throw new internals_1.AmplifyServerContextError({
                message: 'AMPLIFY_APP_ORIGIN environment variable contains an invalid origin string.',
                recoverySuggestion: 'Ensure the AMPLIFY_APP_ORIGIN environment variable is a valid origin string.',
            });
        }
        // OAuth config validation should happen when createAuthRouteHandlers is being called to create
        // Auth API routes.
        (0, internals_1.assertTokenProviderConfig)(resourcesConfig.Auth?.Cognito);
        (0, internals_1.assertOAuthConfig)(resourcesConfig.Auth.Cognito);
        const { userPoolClientId } = resourcesConfig.Auth.Cognito;
        const { oauth: oAuthConfig } = resourcesConfig.Auth.Cognito.loginWith;
        const setCookieOptions = globalSettings.getRuntimeOptions().cookies ?? {};
        // The call-site of this returned function is the Next.js API route file
        return (request, contextOrResponse) => handleRequest({
            request,
            contextOrResponse,
            handlerInput: createAuthRoutesHandlersInput,
            userPoolClientId,
            oAuthConfig,
            setCookieOptions,
            origin: amplifyAppOrigin,
        });
    };
};
exports.createAuthRouteHandlersFactory = createAuthRouteHandlersFactory;
